import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, Loader2, FileText, Download, Copy, Check, CalendarClock, MessageSquare, Rocket, ClipboardList, PhoneCall } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { icon: ClipboardList, label: 'Enter your idea' },
  { icon: MessageSquare, label: 'Answer a few questions' },
  { icon: PhoneCall, label: 'We schedule a call' },
  { icon: Rocket, label: 'We fund & build it with you' },
];

const StepsRibbon = ({ active }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="build-steps">
    {STEPS.map((s, i) => {
      const Icon = s.icon;
      const on = i <= active;
      return (
        <div key={i} className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${on ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200 bg-white'}`}>
          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${on ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
            <Icon className="w-4 h-4" />
          </span>
          <div>
            <div className={`text-[10px] font-bold uppercase tracking-wider ${on ? 'text-emerald-600' : 'text-slate-400'}`}>Step {i + 1}</div>
            <div className="text-xs font-semibold text-slate-700 leading-snug">{s.label}</div>
          </div>
        </div>
      );
    })}
  </div>
);

const BuildWithUs = () => {
  const { user } = useAuth();
  const [stage, setStage] = useState('idea'); // idea | questions | done
  const [idea, setIdea] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [busy, setBusy] = useState(false);
  const [prd, setPrd] = useState('');
  const [slackSent, setSlackSent] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/microsaas/config`).then((r) => r.json()).then((d) => setCalendarUrl(d.calendar_url || '')).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) { setName(user.name || ''); setEmail(user.email || ''); setCompany(user.company || ''); }
  }, [user]);

  const startBuilding = async () => {
    if (idea.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/microsaas/questions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idea }) });
      const d = await r.json();
      setQuestions(d.questions || []);
      setStage('questions');
    } catch (_) {} finally { setBusy(false); }
  };

  const submit = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const payload = {
        idea,
        answers: questions.map((q) => ({ question: q, answer: answers[q] || '' })),
        name, email, company,
      };
      const r = await fetch(`${API}/api/microsaas/submit`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const d = await r.json();
      setPrd(d.prd_markdown || '');
      setSlackSent(!!d.slack_sent);
      if (d.calendar_url) setCalendarUrl(d.calendar_url);
      setStage('done');
    } catch (_) {} finally { setBusy(false); }
  };

  const downloadMd = () => {
    const blob = new Blob([prd], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'forsyt-microsaas-prd.md'; a.click();
    URL.revokeObjectURL(url);
  };

  const activeStep = stage === 'idea' ? 0 : stage === 'questions' ? 1 : 2;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] overflow-hidden" data-testid="build-with-us">
      <div className="px-6 py-5 md:px-8 md:py-6 bg-slate-900 text-white">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300 mb-2">
          <Sparkles className="w-3.5 h-3.5" /> Build with Forsyt
        </div>
        <h2 className="text-2xl md:text-3xl font-extrabold leading-tight">Start building your Micro SaaS with us</h2>
        <p className="text-sm text-slate-300 mt-2 max-w-2xl leading-relaxed">
          Describe your idea. We'll ask a few smart questions, turn it into a crisp PRD, and our team will personally take it from there — including putting our marketing money behind it to help you launch on Forsyt.
        </p>
      </div>

      <div className="p-6 md:p-8 space-y-6">
        <StepsRibbon active={activeStep} />

        {stage === 'idea' && (
          <div className="space-y-4">
            <label className="block text-sm font-semibold text-slate-700">What do you want to build?</label>
            <textarea
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              data-testid="build-idea-input"
              rows={4}
              placeholder="e.g. A tool that auto-summarizes sales call transcripts and flags at-risk deals for RevOps teams…"
              className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 resize-none"
            />
            <button
              onClick={startBuilding}
              disabled={busy || idea.trim().length < 3}
              data-testid="build-start-btn"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-500 disabled:opacity-50 transition-colors"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</> : <>Start building <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}

        {stage === 'questions' && (
          <div className="space-y-5">
            <p className="text-sm text-slate-500">A few quick questions so we scope this properly:</p>
            {questions.map((q, i) => (
              <div key={i} data-testid={`build-question-${i}`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">{i + 1}. {q}</label>
                <textarea
                  value={answers[q] || ''}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q]: e.target.value }))}
                  rows={2}
                  data-testid={`build-answer-${i}`}
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 resize-none"
                />
              </div>
            ))}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="build-name" className="rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email *" data-testid="build-email" className="rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
              <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company" data-testid="build-company" className="rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={submit}
                disabled={busy || !email.trim()}
                data-testid="build-submit-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 disabled:opacity-50 transition-colors"
              >
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating your PRD…</> : <><FileText className="w-4 h-4" /> Generate PRD & send to Forsyt</>}
              </button>
              <button onClick={() => setStage('idea')} className="text-sm font-semibold text-slate-500 hover:text-slate-800">Back</button>
            </div>
          </div>
        )}

        {stage === 'done' && (
          <div className="space-y-5" data-testid="build-done">
            <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
              <Check className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700">
                <strong className="text-slate-900">Your PRD is ready{slackSent ? ' and sent to our team' : ' and saved'}.</strong> We'll review it and reach out at <strong>{email}</strong> to schedule a call.
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Your PRD.md</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => { navigator.clipboard?.writeText(prd); setCopied(true); setTimeout(() => setCopied(false), 1500); }} data-testid="build-copy" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900">
                    {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                  </button>
                  <button onClick={downloadMd} data-testid="build-download" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900">
                    <Download className="w-3.5 h-3.5" /> Download .md
                  </button>
                </div>
              </div>
              <pre className="max-h-72 overflow-y-auto px-4 py-3 text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed">{prd}</pre>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {calendarUrl && (
                <a href={calendarUrl} target="_blank" rel="noreferrer" data-testid="build-book-call" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-500 transition-colors">
                  <CalendarClock className="w-4 h-4" /> Book a call to get started
                </a>
              )}
              <button onClick={() => { setStage('idea'); setIdea(''); setQuestions([]); setAnswers({}); setPrd(''); }} data-testid="build-another" className="text-sm font-semibold text-slate-500 hover:text-slate-800">Submit another idea</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildWithUs;
